// Dino Dig: brush away the rock, assemble the skeleton, then read the bones.
// Pointed teeth mean meat; flat or leaf-shaped teeth mean plants.

import { h, clear } from '@kit/dom';
import { makeSurface, roundRect } from '@kit/canvas';
import { attachPointer } from '@kit/pointer';
import { shuffle, pick } from '@kit/rng';
import type { GameModule, GameContext } from '@shell/types';
import { DINOS, PIECES, PIECE_LABEL, GROUP_LABELS, tierFor, type Dino, type Piece } from './content';
import { art, hasArt } from '@kit/assets';

const COLS = 10, ROWS = 7;
const BONE = '#f1ece6', BONE_EDGE = '#8a5a3b';
/** session-wide: which skeletons she has assembled (the shell does not expose the save) */
const assembled = new Set<string>();

// piece anchor positions in a 200 x 120 local skeleton space (head to the left)
const SLOT: Record<Piece, [number, number]> = { skull: [28, 44], neck: [62, 52], spine: [118, 46], ribs: [118, 72], legs: [128, 100], tail: [186, 50] };

function drawPiece(c: CanvasRenderingContext2D, d: Dino, p: Piece, x: number, y: number, s: number, ghost = false) {
  c.save();
  c.translate(x, y); c.scale(s, s);
  c.lineWidth = 1.6 / s;
  c.strokeStyle = ghost ? 'rgba(42,42,46,.35)' : BONE_EDGE;
  c.fillStyle = ghost ? 'rgba(255,255,255,.25)' : BONE;
  if (ghost) c.setLineDash([3 / s, 3 / s]);
  switch (p) {
    case 'skull': {
      c.beginPath(); c.moveTo(-18, -8); c.lineTo(8, -12); c.lineTo(18, -2); c.lineTo(12, 10); c.lineTo(-14, 8); c.closePath(); c.fill(); c.stroke();
      if (d.feature === 'frill') { c.beginPath(); c.moveTo(8, -12); c.lineTo(22, -24); c.lineTo(28, -6); c.lineTo(18, -2); c.closePath(); c.fill(); c.stroke(); c.beginPath(); c.moveTo(-6, -10); c.lineTo(-10, -22); c.lineTo(-2, -10); c.fill(); c.stroke(); }
      if (d.feature === 'crest') { c.beginPath(); c.moveTo(6, -12); c.lineTo(30, -26); c.lineTo(32, -20); c.lineTo(12, -8); c.closePath(); c.fill(); c.stroke(); }
      // eye and teeth
      c.fillStyle = ghost ? 'transparent' : '#2a2a2e'; c.beginPath(); c.arc(4, -3, 2.2, 0, 7); c.fill();
      c.fillStyle = ghost ? 'transparent' : BONE_EDGE;
      for (let i = 0; i < 5; i++) { const tx = -14 + i * 5; c.beginPath(); c.moveTo(tx, 8); if (d.teeth === 'pointed' || d.teeth === 'conical') { c.lineTo(tx + 1.5, 13); c.lineTo(tx + 3, 8); } else { c.lineTo(tx, 11); c.lineTo(tx + 3, 11); c.lineTo(tx + 3, 8); } c.closePath(); c.fill(); }
      break;
    }
    case 'neck': {
      const n = d.feature === 'longneck' ? 6 : 3;
      for (let i = 0; i < n; i++) { const vx = -14 + i * 7, vy = d.feature === 'longneck' ? 10 - i * 4 : 4 - i * 2; roundRect(c, vx, vy, 6, 7, 2); c.fill(); c.stroke(); }
      break;
    }
    case 'spine': {
      for (let i = 0; i < 8; i++) { roundRect(c, -28 + i * 7.5, -3, 6, 7, 2); c.fill(); c.stroke(); }
      if (d.feature === 'plates') for (let i = 0; i < 4; i++) { c.beginPath(); c.moveTo(-22 + i * 13, -3); c.lineTo(-16 + i * 13, -18 - (i % 2) * 4); c.lineTo(-10 + i * 13, -3); c.closePath(); c.fill(); c.stroke(); }
      if (d.feature === 'sail') for (let i = 0; i < 7; i++) { c.beginPath(); c.moveTo(-26 + i * 8, -3); c.lineTo(-25 + i * 8, -14 - Math.sin(i / 6 * Math.PI) * 14); c.lineTo(-23 + i * 8, -3); c.closePath(); c.fill(); c.stroke(); }
      break;
    }
    case 'ribs': {
      for (let i = 0; i < 6; i++) { c.beginPath(); c.moveTo(-22 + i * 8, -10); c.quadraticCurveTo(-26 + i * 8, 4, -18 + i * 8, 12); c.stroke(); }
      break;
    }
    case 'legs': {
      const big = d.legs === 2;
      // hind legs (always big)
      for (const lx of [8, 16]) { roundRect(c, lx, -16, 6, 16, 2); c.fill(); c.stroke(); roundRect(c, lx + 1, -2, 5, 14, 2); c.fill(); c.stroke(); c.beginPath(); c.moveTo(lx - 2, 12); c.lineTo(lx + 10, 12); c.lineTo(lx + 8, 15); c.lineTo(lx, 15); c.closePath(); c.fill(); c.stroke(); }
      // front legs: tiny arms for two-legged dinos, full legs for four-legged
      for (const lx of [-24, -16]) {
        if (big) { roundRect(c, lx, -16, 3.5, 9, 1.5); c.fill(); c.stroke(); if (d.feature === 'claw') { c.beginPath(); c.moveTo(lx, -7); c.lineTo(lx - 3, -2); c.lineTo(lx + 2, -6); c.fill(); c.stroke(); } }
        else { roundRect(c, lx, -16, 5, 15, 2); c.fill(); c.stroke(); roundRect(c, lx + 0.5, -2, 4.5, 13, 2); c.fill(); c.stroke(); c.beginPath(); c.moveTo(lx - 2, 11); c.lineTo(lx + 7, 11); c.lineTo(lx + 6, 14); c.lineTo(lx - 1, 14); c.closePath(); c.fill(); c.stroke(); }
      }
      break;
    }
    case 'tail': {
      const n = 7;
      for (let i = 0; i < n; i++) { const w = 6 - i * 0.6; roundRect(c, -24 + i * 7, -w / 2 + (d.feature === 'sail' ? 0 : -i * 1.2), 6, w, 1.5); c.fill(); c.stroke(); }
      if (d.feature === 'club') { c.beginPath(); c.arc(28, -7, 7, 0, 7); c.fill(); c.stroke(); }
      if (d.feature === 'plates') for (let i = 0; i < 2; i++) { c.beginPath(); c.moveTo(16 + i * 8, -6); c.lineTo(20 + i * 8, -18); c.lineTo(22 + i * 8, -6); c.fill(); c.stroke(); }
      break;
    }
  }
  c.restore();
}

interface Q { text: string; choices: string[]; answer: number; why: string }

function questionsFor(d: Dino, tier: 1 | 2 | 3, rng: () => number): Q[] {
  const qs: Q[] = [];
  const dietQ: Q = { text: `${d.teethText} What did ${d.name} eat?`, choices: ['Meat', 'Plants'], answer: d.diet === 'meat' ? 0 : 1, why: d.diet === 'meat' ? 'Pointed, sharp teeth are for tearing meat. Plant eaters have flat or leaf-shaped teeth for grinding.' : 'Flat and leaf-shaped teeth are for cutting and grinding plants. Meat eaters need sharp points.' };
  qs.push(dietQ);
  if (tier === 1) {
    qs.push({ text: `Look at the legs. How did ${d.name} walk?`, choices: ['On two legs', 'On four legs'], answer: d.legs === 2 ? 0 : 1, why: d.legs === 2 ? 'The back legs are long and strong and the front ones are small, so it stood on two legs.' : 'All four legs are thick and about the same length, so it walked on four.' });
  }
  if (tier >= 2) {
    const others = shuffle(rng, (Object.keys(GROUP_LABELS) as (keyof typeof GROUP_LABELS)[]).filter(g => g !== d.group)).slice(0, 3).map(g => GROUP_LABELS[g]);
    const choices = shuffle(rng, [d.groupLabel, ...others]);
    qs.push({ text: `Which group does ${d.name} belong to?`, choices, answer: choices.indexOf(d.groupLabel), why: `${d.name} is a ${d.groupLabel.toLowerCase()}. ${d.fact}` });
  }
  if (tier === 3) {
    const pool: Q[] = [
      { text: 'How do we know whether a dinosaur walked on two legs or four?', choices: ['Compare the front and back leg bones', 'Count its teeth', 'Measure its skull', 'Look at its color'], answer: 0, why: 'Short front limbs and long, heavy back limbs mean two legs. Four matching legs mean four.' },
      { text: 'A fossil is found in rock that formed on a river bottom. What does that tell us?', choices: ['It lived or died near water', 'It could fly', 'It was the biggest dinosaur', 'It ate rocks'], answer: 0, why: 'The rock a fossil sits in tells us about the place it was buried. River rock means a river.' },
      { text: `${d.name} lived ${d.era}. How do scientists know how old a fossil is?`, choices: ['From the age of the rock layers around it', 'From how dusty it is', 'By asking a museum', 'From its size'], answer: 0, why: 'Deeper layers are older, and some rocks can be dated by the atoms inside them.' },
      { text: 'Bones alone cannot tell us one of these. Which one?', choices: ['Its skin color', 'How many legs it walked on', 'What it ate', 'How big it was'], answer: 0, why: 'Teeth tell diet, limbs tell how it walked, length tells size. Color almost never fossilizes.' },
    ];
    qs.push(pick(rng, pool));
  }
  return qs;
}

function mount(host: HTMLElement, ctx: GameContext) {
  const level = ctx.currentLevel();
  const tier = tierFor(level);
  const dinos = shuffle(ctx.rng, DINOS).slice(0, 2);
  let di = 0;
  let attempts = 0, correct = 0;
  const t0 = performance.now();

  const head = h('div.row', { style: { justifyContent: 'space-between' } }, h('span.muted', null, 'Dig ', h('strong', null, '1'), ' of 2'), h('span.muted.small', null, ''));
  const stage = h('div');
  const panel = h('div.stack');
  host.append(head, stage, panel);
  let S = makeSurface(stage, { aspect: 0.66, onResize: () => drawPhase() });
  let detach: (() => void) | null = null;
  let drawPhase: () => void = () => { /* set per phase */ };

  const skelId = (d: Dino) => `dinodig.skeleton.${d.name.toLowerCase()}`;
  const paintedFor = (d: Dino) => hasArt('dinodig.site') && hasArt(skelId(d));

  /** where the painted skeleton sits: contain-fit inside the pit of the dig-site painting */
  function skelRect(d: Dino, W: number, H: number) {
    const img = art(skelId(d))!;
    const bx = W * 0.14, by = H * 0.14, bw = W * 0.72, bh = H * 0.72;
    const s = Math.min(bw / img.naturalWidth, bh / img.naturalHeight);
    const w = img.naturalWidth * s, h = img.naturalHeight * s;
    return { x: bx + (bw - w) / 2, y: by + (bh - h) / 2, w, h };
  }

  function startDig() {
    const d = dinos[di];
    if (paintedFor(d)) { startPaintedDig(d); return; }
    (head.querySelector('strong') as HTMLElement).textContent = String(di + 1);
    (head.lastElementChild as HTMLElement).textContent = 'Brush away the rock';
    clear(panel);
    panel.append(h('p.center', { style: { fontWeight: '800' } }, 'Rub your finger across the rock to uncover the bones.'));
    const revealed = new Uint8Array(COLS * ROWS);
    let done = false;
    const seedShade = Array.from({ length: COLS * ROWS }, () => 0.85 + ctx.rng() * 0.3);
    detach?.();
    detach = attachPointer(S.canvas, {
      down: p => brush(p.x, p.y), move: (p, e) => { if (e.buttons || e.pointerType === 'touch') brush(p.x, p.y); },
    });
    function brush(x: number, y: number) {
      if (done) return;
      const cw = S.w / COLS, ch = S.h / ROWS;
      const cx = Math.floor(x / cw), cy = Math.floor(y / ch);
      let changed = false;
      for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) {
        if (Math.abs(dx) + Math.abs(dy) > 1) continue;
        const gx = cx + dx, gy = cy + dy;
        if (gx < 0 || gy < 0 || gx >= COLS || gy >= ROWS) continue;
        if (!revealed[gy * COLS + gx]) { revealed[gy * COLS + gx] = 1; changed = true; }
      }
      if (changed) { ctx.sound('tick'); drawPhase(); }
      let n = 0; for (let i = 0; i < revealed.length; i++) n += revealed[i];
      if (n >= COLS * ROWS * 0.82) { done = true; ctx.sound('ding'); ctx.say('Bones! Now put the skeleton together.'); setTimeout(startAssemble, 900); }
    }
    drawPhase = () => {
      const c = S.ctx, W = S.w, H = S.h;
      c.fillStyle = '#c9a87c'; c.fillRect(0, 0, W, H);
      drawSkeleton(c, d, W, H, 1);
      const cw = W / COLS, ch = H / ROWS;
      for (let gy = 0; gy < ROWS; gy++) for (let gx = 0; gx < COLS; gx++) {
        if (revealed[gy * COLS + gx]) continue;
        const sh = seedShade[gy * COLS + gx];
        c.fillStyle = `rgb(${Math.round(138 * sh)},${Math.round(90 * sh)},${Math.round(59 * sh)})`;
        c.fillRect(gx * cw, gy * ch, cw + 0.5, ch + 0.5);
        c.fillStyle = 'rgba(0,0,0,.08)'; c.fillRect(gx * cw + cw * 0.2, gy * ch + ch * 0.3, cw * 0.25, ch * 0.2);
      }
    };
    drawPhase();
  }

  // ---- painted dig: the painting peels away cell by cell to show the real skeleton ----
  function startPaintedDig(d: Dino) {
    const PC = 18, PR = 12;
    (head.querySelector('strong') as HTMLElement).textContent = String(di + 1);
    (head.lastElementChild as HTMLElement).textContent = 'Brush away the rock';
    clear(panel);
    const msg = h('p.center', { style: { fontWeight: '800' } }, 'Rub your finger across the dig to uncover the bones.');
    const meter = h('div', { style: { height: '10px', borderRadius: '5px', background: 'var(--surface-2)', overflow: 'hidden' } }, h('div', { style: { height: '100%', width: '0%', background: '#b48a5a', transition: 'width .2s' } }));
    panel.append(msg, meter);
    const revealed = new Uint8Array(PC * PR);
    let boneCells: number[] = [];
    let measuredFor = '';
    let done = false;

    // which cells actually contain bone: sample the skeleton's alpha at the drawn size
    function measure() {
      const W = S.w, H = S.h, key = `${W}x${H}`;
      if (measuredFor === key || W < 16) return;
      measuredFor = key;
      const r = skelRect(d, W, H);
      const off = document.createElement('canvas'); off.width = Math.ceil(W); off.height = Math.ceil(H);
      const oc = off.getContext('2d')!; oc.drawImage(art(skelId(d))!, r.x, r.y, r.w, r.h);
      const data = oc.getImageData(0, 0, off.width, off.height).data;
      const cw = W / PC, ch = H / PR;
      boneCells = [];
      for (let gy = 0; gy < PR; gy++) for (let gx = 0; gx < PC; gx++) {
        let hit = 0, n = 0;
        for (let sy = 0; sy < 4; sy++) for (let sx = 0; sx < 4; sx++) {
          const px = Math.floor(gx * cw + (sx + 0.5) * cw / 4), py = Math.floor(gy * ch + (sy + 0.5) * ch / 4);
          n++; if (data[(py * off.width + px) * 4 + 3] > 60) hit++;
        }
        if (hit / n >= 0.12) boneCells.push(gy * PC + gx);
      }
    }

    detach?.();
    detach = attachPointer(S.canvas, {
      down: p => brush(p.x, p.y), move: (p, e) => { if (e.buttons || e.pointerType === 'touch') brush(p.x, p.y); },
    });
    function brush(x: number, y: number) {
      if (done) return;
      measure();
      const cw = S.w / PC, ch = S.h / PR;
      const cx = Math.floor(x / cw), cy = Math.floor(y / ch);
      let changed = false;
      for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) {
        const gx = cx + dx, gy = cy + dy;
        if (gx < 0 || gy < 0 || gx >= PC || gy >= PR) continue;
        if (!revealed[gy * PC + gx]) { revealed[gy * PC + gx] = 1; changed = true; }
      }
      if (!changed) return;
      ctx.sound('tick');
      const found = boneCells.filter(i => revealed[i]).length / Math.max(1, boneCells.length);
      (meter.firstElementChild as HTMLElement).style.width = `${Math.round(Math.min(1, found / 0.7) * 100)}%`;
      if (found >= 0.7) finish();
      drawPhase();
    }
    function finish() {
      done = true;
      // the rest of the rock falls away in a quick sweep
      let k = 0;
      const sweep = setInterval(() => {
        for (let i = 0; i < PC; i++) revealed[(k % PR) * PC + i] = 1;
        k++; drawPhase();
        if (k >= PR) clearInterval(sweep);
      }, 45);
      ctx.sound('chime');
      msg.textContent = `You found a ${d.name}!`;
      ctx.say(`A whole skeleton! It is ${d.name}. Now read the bones.`);
      if (!assembled.has(d.name)) { assembled.add(d.name); ctx.keep('creature', { name: d.name }); }
      if (assembled.size >= DINOS.length) ctx.charm('charm.dino8');
      setTimeout(startQuestions, 1600);
    }
    drawPhase = () => {
      const c = S.ctx, W = S.w, H = S.h;
      if (W < 16) return;
      const site = art('dinodig.site')!;
      // dug-out ground: the painting, darkened, with the skeleton lying in it
      c.drawImage(site, 0, 0, W, H);
      c.fillStyle = 'rgba(120,80,40,.35)'; c.fillRect(0, 0, W, H);
      const r = skelRect(d, W, H);
      c.drawImage(art(skelId(d))!, r.x, r.y, r.w, r.h);
      // untouched ground on top, cut from the same painting so digging looks like peeling it back
      const cw = W / PC, ch = H / PR, sx = site.naturalWidth / W, sy = site.naturalHeight / H;
      for (let gy = 0; gy < PR; gy++) for (let gx = 0; gx < PC; gx++) {
        if (revealed[gy * PC + gx]) continue;
        c.drawImage(site, gx * cw * sx, gy * ch * sy, cw * sx, ch * sy, gx * cw, gy * ch, cw + 0.6, ch + 0.6);
      }
    };
    measure();
    drawPhase();
  }

  function skeletonScale(W: number, H: number) { return Math.min(W / 220, H / 140) * 0.9; }
  function drawSkeleton(c: CanvasRenderingContext2D, d: Dino, W: number, H: number, alpha: number, only?: Set<Piece>, ghost?: Set<Piece>) {
    const s = skeletonScale(W, H);
    const ox = W / 2 - 100 * s, oy = H / 2 - 60 * s;
    c.save(); c.globalAlpha = alpha;
    for (const p of PIECES) {
      const [px, py] = SLOT[p];
      if (ghost?.has(p)) drawPiece(c, d, p, ox + px * s, oy + py * s, s, true);
      else if (!only || only.has(p)) drawPiece(c, d, p, ox + px * s, oy + py * s, s);
    }
    c.restore();
  }

  function startAssemble() {
    const d = dinos[di];
    (head.lastElementChild as HTMLElement).textContent = 'Build the skeleton';
    clear(panel);
    const hint = h('p.center', { style: { fontWeight: '800' } }, 'Drag each bone to its dashed outline.');
    panel.append(hint);
    const placed = new Set<Piece>();
    const order = shuffle(ctx.rng, [...PIECES]);
    let carrying: Piece | null = null;
    let cx = 0, cy = 0;
    const trayPos = (i: number, W: number, H: number): [number, number] => [W * (0.1 + 0.16 * i), H * 0.9];
    detach?.();
    detach = attachPointer(S.canvas, {
      down(p) {
        const W = S.w, H = S.h, s = skeletonScale(W, H) * 0.55;
        for (let i = order.length - 1; i >= 0; i--) {
          const pc = order[i]; if (placed.has(pc)) continue;
          const [tx, ty] = trayPos(i, W, H);
          if (Math.hypot(p.x - tx, p.y - ty) < Math.max(26, 30 * s)) { carrying = pc; cx = p.x; cy = p.y; drawPhase(); return; }
        }
      },
      move(p) { if (carrying) { cx = p.x; cy = p.y; drawPhase(); } },
      up(p) {
        if (!carrying) return;
        const W = S.w, H = S.h, s = skeletonScale(W, H);
        const ox = W / 2 - 100 * s, oy = H / 2 - 60 * s;
        let target: Piece | null = null, bd = 1e9;
        for (const pc of PIECES) { if (placed.has(pc)) continue; const [sx, sy] = SLOT[pc]; const dd = Math.hypot(p.x - (ox + sx * s), p.y - (oy + sy * s)); if (dd < bd) { bd = dd; target = pc; } }
        if (target && bd < Math.max(34, 28 * s)) {
          if (target === carrying) {
            placed.add(carrying); ctx.sound('pop'); hint.textContent = `${PIECE_LABEL[carrying]} in place.`;
            if (placed.size === PIECES.length) { ctx.sound('chime'); hint.textContent = `${d.name}! Now read the bones.`; if (!assembled.has(d.name)) { assembled.add(d.name); ctx.keep('creature', { name: d.name }); } if (assembled.size >= DINOS.length) ctx.charm('charm.dino8'); carrying = null; drawPhase(); setTimeout(startQuestions, 1100); return; }
          } else {
            ctx.sound('buzz'); hint.textContent = `That is the ${PIECE_LABEL[carrying].toLowerCase()}. ${where(carrying)}`;
          }
        }
        carrying = null; drawPhase();
      },
    });
    function where(p: Piece): string {
      return p === 'skull' ? 'It goes at the front.' : p === 'tail' ? 'It goes at the back.' : p === 'legs' ? 'They go underneath.' : p === 'ribs' ? 'They hang below the spine.' : p === 'neck' ? 'It joins the skull to the spine.' : 'It runs along the top of the body.';
    }
    drawPhase = () => {
      const c = S.ctx, W = S.w, H = S.h;
      c.fillStyle = '#efe9db'; c.fillRect(0, 0, W, H);
      c.fillStyle = '#e4cfa0'; c.fillRect(0, H * 0.8, W, H * 0.2);
      const ghost = new Set<Piece>(PIECES.filter(p => !placed.has(p)));
      drawSkeleton(c, d, W, H, 1, placed, ghost);
      const s = skeletonScale(W, H) * 0.55;
      order.forEach((pc, i) => {
        if (placed.has(pc) || pc === carrying) return;
        const [tx, ty] = trayPos(i, W, H);
        drawPiece(c, d, pc, tx, ty, s);
        c.fillStyle = '#2a2a2e'; c.font = '700 10px Nunito, sans-serif'; c.textAlign = 'center'; c.fillText(PIECE_LABEL[pc], tx, H * 0.985);
      });
      if (carrying) drawPiece(c, d, carrying, cx, cy, skeletonScale(W, H));
    };
    drawPhase();
  }

  function startQuestions() {
    const d = dinos[di];
    (head.lastElementChild as HTMLElement).textContent = 'Read the bones';
    detach?.(); detach = null;
    const qs = questionsFor(d, tier, ctx.rng);
    let qi = 0;
    drawPhase = () => {
      const c = S.ctx, W = S.w, H = S.h;
      if (paintedFor(d)) {
        c.drawImage(art('dinodig.site')!, 0, 0, W, H);
        c.fillStyle = 'rgba(120,80,40,.35)'; c.fillRect(0, 0, W, H);
        const r = skelRect(d, W, H); c.drawImage(art(skelId(d))!, r.x, r.y, r.w, r.h);
        return;
      }
      c.fillStyle = '#efe9db'; c.fillRect(0, 0, W, H); drawSkeleton(c, d, W, H, 1);
    };
    drawPhase();
    showQ();
    function showQ() {
      const q = qs[qi];
      clear(panel);
      const tq = performance.now();
      let missed = false;
      panel.append(h('p.center', { style: { fontWeight: '800' } }, q.text));
      const grid = h('div.grid2', null, ...q.choices.map((ch, i) => h('button.choice', { style: { fontSize: '16px', padding: '6px' }, onclick: () => pickAnswer(i) }, ch)));
      panel.append(grid);
      function pickAnswer(i: number) {
        if (i === q.answer) {
          attempts++; if (!missed) correct++;
          ctx.report({ skill: 'paleontology', level, correct: !missed, hintUsed: missed, ms: performance.now() - tq, problemId: `dino:${d.name}:${qi}` });
          ctx.sound('ding'); ctx.say(q.why);
          qi++;
          if (qi >= qs.length) setTimeout(reveal, 1500); else setTimeout(showQ, 1500);
        } else {
          if (!missed) { missed = true; ctx.report({ skill: 'paleontology', level, correct: false, hintUsed: false, ms: performance.now() - tq, problemId: `dino:${d.name}:${qi}` }); attempts++; }
          ctx.sound('buzz');
          ctx.say(qi === 0 ? (d.diet === 'meat' ? 'Look at the points on those teeth. What are sharp teeth for?' : 'Those teeth are flat or leaf-shaped. What needs grinding?') : 'Not that one. Look again at the skeleton and try another.');
          (grid.children[i] as HTMLElement).style.opacity = '.4';
        }
      }
    }
  }

  function reveal() {
    const d = dinos[di];
    clear(panel);
    panel.append(h('div.card', { style: { borderLeft: `8px solid ${d.color}` } },
      h('h3', null, d.name, h('span.muted.small', null, `  (${d.say})`)),
      h('p.small', null, `${d.groupLabel}. ${d.era}.`),
      h('p.small', null, d.size + '.'),
      h('p', { style: { fontWeight: '700', marginTop: '6px' } }, d.fact),
    ));
    ctx.speak(d.name);
    di++;
    if (di >= dinos.length) {
      panel.append(h('p.center.muted.small', null, 'Round complete.'));
      setTimeout(() => ctx.end({ attempts, correct, perfect: attempts === correct, durationMs: performance.now() - t0, notes: [`Skeletons assembled this session: ${assembled.size} of ${DINOS.length}`] }), 2200);
    } else {
      panel.append(h('button.btn.primary.block', { onclick: startDig }, 'Dig again'));
    }
  }

  startDig();
  return { destroy() { detach?.(); S.destroy(); clear(host); } };
}

const mod: GameModule = {
  id: 'science.dino-dig',
  title: 'Dino Dig',
  blurb: 'Brush away the rock, uncover a skeleton, and read what the bones say.',
  category: 'science',
  skills: ['paleontology'],
  levelRange: [1, 10],
  unlock: { stars: 10 },
  mount,
};
export default mod;
